import type { StatusCategoryKeys } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import type { Color } from '@atlassian/jira-issue-epic-color/src/common/types.tsx';

export type IdGql = string;

export const SPRINTS_FEATURE = 'SPRINTS' as const;
export const ESTIMATION_FEATURE = 'ESTIMATION' as const;
export const VERSION_FEATURE = 'RELEASES' as const;

export const ENABLED = 'ENABLED' as const;
export const DISABLED = 'DISABLED' as const;
export const COMING_SOON = 'COMING_SOON' as const;

export type FeatureStatusGql = typeof ENABLED | typeof DISABLED | typeof COMING_SOON;

export type ChildIssuesMetadataGql = {
	complete: number;
	total: number;
};

export type StatusGql = {
	id: string;
	name: string;
	category: StatusCategoryKeys;
};

export type IssueTypeGql = {
	id: IdGql;
	name: string;
	iconUrl: string;
	__typename?: 'IssueType';
};

export type JiraUserGql = {
	accountId: string;
	displayName: string;
	avatarUrl: string;
};

export type IssueGql = {
	id: string;
	key: string;
	summary: string;
	labels: string[];
	type: IssueTypeGql;
	status: StatusGql;
	assignee: JiraUserGql | null;
};

export type EstimateGql = {
	storyPoints: number | null;
	originalEstimate: {
		value: number;
		valueAsText: string;
	} | null;
};

export type PriorityGql = {
	name: string;
	iconUrl: string;
};

export type CardGql = {
	id: string;
	issue: IssueGql;
	flagged: boolean;
	done: boolean;
	parentId: string | null;
	estimate: EstimateGql | null;
	priority: PriorityGql | null;
	dueDate: string | null;
	childIssuesMetadata: ChildIssuesMetadataGql | null;
	fixVersions: string[];
};

export type ChildrenInfoStatsGql = {
	issueCount: number;
	__typename?: 'ChildrenInfoStats';
};

export type ChildrenInfoGql = {
	todoStats: ChildrenInfoStatsGql;
	inProgressStats: ChildrenInfoStatsGql;
	doneStats: ChildrenInfoStatsGql;
	__typename?: 'ChildrenInfo';
};

export type IssueParentGql = {
	id: IdGql;
	key: string;
	summary: string;
	issueType: IssueTypeGql;
	color: Color;
	childrenInfo: ChildrenInfoGql;
	startDate: string;
	dueDate: string;
	__typename?: 'IssueParent';
};

export type FeatureGql = {
	key: string;
	status: FeatureStatusGql;
};

export type BoardGql = {
	issueParents: IssueParentGql[];
	features: FeatureGql[];
};

export type AgileGql = {
	board: BoardGql;
};

export type DataGql = {
	agile: AgileGql | null;
};

export type ErrorLocationGql = {
	line: number;
	column: number;
};

export type ErrorGql = {
	message: string;
	locations: ErrorLocationGql[];
	path: string[];
	extensions: {
		errorType: string | undefined | null;
	};
};

export type ResponseGql = {
	errors?: ErrorGql[];
	data: DataGql | null;
};

export type ExtensionIconGql = {
	url: string;
};

export type OperationGql = {
	name: string;
	url: string;
	tooltip: string;
	styleClass: string;
	icon: ExtensionIconGql;
};

export type ExtensionGql = {
	operations: OperationGql[];
};

// move to non-critical
export type ExtensionLoadGql = {
	extension: ExtensionGql;
};

export type FilteredCardIdsGql = string[];
export type FilteredCardIdsLoadGql = {
	boardScope: {
		filteredCardIds: FilteredCardIdsGql;
	};
	errors?: ErrorGql[];
};
